import React from 'react';
import { Link } from 'react-router-dom';
import { useCompare } from '@/contexts/CompareContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, X, CheckCircle, XCircle, Plus, ArrowRight } from 'lucide-react';

const ComparePage = () => {
  const { compareList, removeFromCompare, clearCompare } = useCompare();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const comparisonRows = [
    { label: 'Premium (Monthly)', key: 'premium_monthly', format: formatCurrency, highlight: true },
    { label: 'Premium (Yearly)', key: 'premium_yearly', format: formatCurrency, highlight: true },
    { label: 'Coverage Amount', key: 'coverage_amount', format: formatCurrency, highlight: true },
    { label: 'Cashless Facility', key: 'features.cashless', type: 'boolean' },
    { label: 'Pre-existing Cover', key: 'features.pre_existing', type: 'boolean' },
    { label: 'Network Size', key: 'features.network_size', format: (val) => val ? `${val.toLocaleString()} hospitals` : 'N/A' },
    { label: 'Claim Settlement Ratio', key: 'features.claim_settlement_ratio', format: (val) => val ? `${val}%` : 'N/A' },
    { label: 'Waiting Period', key: 'features.waiting_period_days', format: (val) => val ? `${val} days` : 'N/A' }
  ];

  // Normalize policy data to handle both user policies and marketplace policies
  const normalizePolicy = (policy) => {
    if (policy.isUserPolicy) {
      // User policy structure
      return {
        ...policy,
        name: policy.insurer_name,
        provider_name: policy.insurer_name,
        category_type: policy.policy_type,
        premium_yearly: policy.premium || 0,
        coverage_amount: 0, // User policies don't have coverage amount from their form
        features: {
          cashless: false,
          pre_existing: false,
          network_size: null,
          claim_settlement_ratio: null,
          waiting_period_days: null
        }
      };
    }
    // Marketplace policy - already in correct format
    return policy;
  };

  const getValue = (policy, key) => {
    // Special handling for monthly premium
    if (key === 'premium_monthly') {
      return policy.premium_yearly / 12;
    }
    
    const keys = key.split('.');
    let value = policy;
    for (const k of keys) {
      value = value?.[k];
    }
    return value;
  };

  if (compareList.length === 0) {
    return (
      <div className="min-h-screen bg-secondary-50" data-testid="compare-page">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-12">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mb-4">
                <Plus className="h-8 w-8 text-secondary-400" />
              </div>
              <h2 className="text-2xl font-semibold text-secondary-900 mb-2">No policies to compare</h2>
              <p className="text-base text-secondary-600 mb-6 text-center">
                Add policies from the listing page to start comparing
              </p>
              <Link to="/policies">
                <Button data-testid="browse-policies-button">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Browse Policies
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50" data-testid="compare-page">
      {/* Header */}
      <div className="bg-white border-b border-secondary-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <Link to="/policies">
                <Button variant="ghost" size="sm" className="mb-4" data-testid="back-to-policies-button">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Policies
                </Button>
              </Link>
              <h1 className="text-3xl sm:text-4xl font-bold font-heading text-secondary-900 mb-2">
                Compare Policies Side-by-Side
              </h1>
              <p className="text-lg text-secondary-600">
                Comparing {compareList.length} {compareList.length === 1 ? 'policy' : 'policies'} - Make the right choice
              </p>
            </div>
            <Button variant="outline" onClick={clearCompare} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="clear-comparison-button">
              <X className="mr-2 h-4 w-4" />
              Clear All
            </Button>
          </div>

          {/* Trust Info */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
              <CheckCircle className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-secondary-700">Side-by-Side Comparison</span>
            </div>
            <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-secondary-700">Clear Feature Analysis</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
        {/* Mobile View - Cards */}
        <div className="lg:hidden space-y-6">
          {compareList.map((policy) => {
            const normalizedPolicy = normalizePolicy(policy);
            return (
              <Card key={`${policy.id}-${policy.isUserPolicy ? 'user' : 'market'}`} className="border-2 border-secondary-200 hover:border-primary-400 transition-all" data-testid={`policy-compare-card-${policy.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary">{normalizedPolicy.category_type}</Badge>
                        {policy.isUserPolicy && (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs">
                            Your Policy
                          </Badge>
                        )}
                        {normalizedPolicy.is_popular && !policy.isUserPolicy && (
                          <Badge className="bg-green-500 text-white hover:bg-green-500 text-xs">
                            Popular
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-secondary-900 mb-1">
                        {normalizedPolicy.name}
                      </h3>
                      <p className="text-sm text-secondary-600 font-medium">{normalizedPolicy.provider_name}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFromCompare(policy.id, policy.isUserPolicy)}
                      className="hover:bg-red-100"
                      data-testid={`remove-policy-${policy.id}`}
                    >
                      <X className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {comparisonRows.map((row, index) => {
                      const value = getValue(normalizedPolicy, row.key);
                      let displayValue;

                      if (row.type === 'boolean') {
                        displayValue = value ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-medium">Yes</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-secondary-400">
                            <XCircle className="h-5 w-5" />
                            <span>No</span>
                          </div>
                        );
                      } else if (row.format) {
                        displayValue = row.format(value);
                      } else {
                        displayValue = value || 'N/A';
                      }

                      return (
                        <div key={index} className={`flex items-center justify-between py-3 ${index === 0 ? 'border-t-2' : 'border-t'} border-secondary-200`}>
                          <span className="text-sm font-semibold text-secondary-700">{row.label}</span>
                          <span className={`text-sm ${row.highlight ? 'text-xl font-bold font-mono text-primary-700' : 'text-secondary-900 font-medium'}`}>
                            {typeof displayValue === 'string' ? displayValue : displayValue}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {!policy.isUserPolicy && (
                    <Link to={`/policy/${policy.id}`} className="mt-6 block">
                      <Button variant="default" className="w-full bg-primary-600 hover:bg-primary-700" data-testid={`view-details-${policy.id}`}>
                        View Full Details
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  )}
                  {policy.isUserPolicy && (
                    <Link to={`/my-policies/${policy.id}`} className="mt-6 block">
                      <Button variant="outline" className="w-full" data-testid={`view-details-${policy.id}`}>
                        View Your Policy
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Desktop View - Table */}
        <div className="hidden lg:block overflow-x-auto">
          <Card>
            <div className="min-w-full">
              <Table data-testid="comparison-table">
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-primary-50 to-blue-50 border-b-2 border-primary-300">
                    <TableHead className="sticky left-0 z-10 bg-gradient-to-r from-primary-50 to-blue-50 font-bold text-secondary-900 w-48 text-base">
                      Features
                    </TableHead>
                    {compareList.map((policy) => {
                      const normalizedPolicy = normalizePolicy(policy);
                      return (
                        <TableHead key={`${policy.id}-${policy.isUserPolicy ? 'user' : 'market'}`} className="min-w-[250px]">
                          <div className="space-y-3 py-2">
                            <div className="flex items-center justify-between">
                              <Badge variant="secondary" className="text-xs">
                                {normalizedPolicy.category_type}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 hover:bg-red-100"
                                onClick={() => removeFromCompare(policy.id, policy.isUserPolicy)}
                                data-testid={`remove-policy-${policy.id}`}
                              >
                                <X className="h-3 w-3 text-red-600" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2">
                              {policy.isUserPolicy && (
                                <Badge className="bg-blue-500 text-white hover:bg-blue-500 text-xs">
                                  Your Policy
                                </Badge>
                              )}
                              {normalizedPolicy.is_popular && !policy.isUserPolicy && (
                                <Badge className="bg-green-500 text-white hover:bg-green-500 text-xs">
                                  Popular Choice
                                </Badge>
                              )}
                            </div>
                            <div className="text-left">
                              <p className="font-bold text-secondary-900 text-lg leading-tight mb-1">{normalizedPolicy.name}</p>
                              <p className="text-sm text-secondary-600 font-medium">{normalizedPolicy.provider_name}</p>
                            </div>
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonRows.map((row, rowIndex) => (
                    <TableRow 
                      key={rowIndex} 
                      className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-secondary-50'} hover:bg-primary-50 transition-colors`}
                    >
                      <TableCell className="sticky left-0 z-10 font-semibold text-secondary-900 bg-inherit border-r-2 border-secondary-200">
                        {row.label}
                      </TableCell>
                      {compareList.map((policy) => {
                        const normalizedPolicy = normalizePolicy(policy);
                        const value = getValue(normalizedPolicy, row.key);
                        let displayValue;

                        if (row.type === 'boolean') {
                          displayValue = value ? (
                            <div className="flex items-center justify-center gap-1 text-green-600">
                              <CheckCircle className="h-5 w-5" />
                              <span className="font-medium">Yes</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-1 text-secondary-400">
                              <XCircle className="h-5 w-5" />
                              <span>No</span>
                            </div>
                          );
                        } else if (row.format) {
                          displayValue = row.format(value);
                        } else {
                          displayValue = value || 'N/A';
                        }

                        return (
                          <TableCell key={`${policy.id}-${policy.isUserPolicy ? 'user' : 'market'}`} className="text-center py-4">
                            {typeof displayValue === 'string' ? (
                              <span className={row.highlight ? 'font-bold text-2xl font-mono text-primary-700' : 'text-base text-secondary-700'}>
                                {displayValue}
                              </span>
                            ) : (
                              displayValue
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  <TableRow className="bg-gradient-to-r from-secondary-50 to-white border-t-2 border-secondary-300">
                    <TableCell className="sticky left-0 z-10 font-bold text-secondary-900 bg-gradient-to-r from-secondary-50 to-white border-r-2 border-secondary-200 text-base">
                      Actions
                    </TableCell>
                    {compareList.map((policy) => (
                      <TableCell key={`${policy.id}-${policy.isUserPolicy ? 'user' : 'market'}`} className="text-center py-6">
                        {!policy.isUserPolicy ? (
                          <Link to={`/policy/${policy.id}`}>
                            <Button variant="default" size="lg" className="w-full bg-primary-600 hover:bg-primary-700" data-testid={`view-details-${policy.id}`}>
                              View Full Details
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/my-policies/${policy.id}`}>
                            <Button variant="outline" size="lg" className="w-full" data-testid={`view-details-${policy.id}`}>
                              View Your Policy
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>

        {/* Add More Policies CTA */}
        {compareList.length < 4 && (
          <Card className="mt-6">
            <CardContent className="py-8 text-center">
              <p className="text-base text-secondary-600 mb-4">
                You can compare up to 4 policies. Add {4 - compareList.length} more {4 - compareList.length === 1 ? 'policy' : 'policies'}.
              </p>
              <Link to="/policies">
                <Button data-testid="add-more-policies-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Add More Policies
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ComparePage;
